import { getBetterAuthBaseUrl } from "@-/dev.improve.haniverse.lib.auth.client";
import {
  type ActorReplicaExport,
  exportActorReplica,
} from "@-/dev.improve.haniverse.lib.database.engine";
import { sendActorSyncImport } from "@-/dev.improve.haniverse.lib.database.engine/import-client";
import { getDeviceAccount } from "@-/dev.improve.haniverse.lib.device-settings";
import { logger } from "@-/dev.improve.haniverse.lib.logger";
import type { GuestHandoffCarried } from "#open-with-guest-handoff";
import { isActorSyncProvisioned, resolveActorSyncGatewayUrl } from "#resolve-actor-sync-url";

/**
 * Sign-up's half of the import (see `database.md`, "Accounts and guests"): the signed-out study,
 * read row by row from the guest database, sent to the account's cloud copy by column name. The
 * gateway applies every row as create-or-revive, so a row the account already holds is left
 * alone, and the import is named by the guest's contents, so sending it again after a crash or an
 * outage changes nothing. The caller pulls afterwards and folds in what an insert cannot merge
 * (the guest's level packs, `retainGuestCustomLevelPacks`).
 *
 * `read` runs one query against the guest database and returns its rows; the platform decides how
 * the guest database is opened (the moved-aside file on native, `guest.db` on web).
 */
export async function carryGuestStudyThroughImport(
  read: (sql: string) => Promise<unknown[]>,
): Promise<GuestHandoffCarried> {
  const account = getDeviceAccount();
  if (account === null || !isActorSyncProvisioned(account)) {
    throw new Error("The guest study can only be carried into a provisioned account");
  }
  const exported: ActorReplicaExport = await exportActorReplica(
    { all: read },
    { kind: "signup", name: account.ownerId ?? "account" },
  );
  const guestUser = exported.changes.find(
    (change) => change.table === "user" && change.kind !== "delete",
  );
  const packs =
    guestUser !== undefined && "row" in guestUser ? guestUser.row["custom_level_packs"] : null;
  const carried: GuestHandoffCarried = {
    guestCustomLevelPacks: typeof packs === "string" ? packs : null,
  };
  if (exported.changes.length === 0) {
    logger(import.meta.location).info("actor-db:guest-carry", { rows: 0 });
    return carried;
  }
  const sent = await sendActorSyncImport({
    authToken: account.tursoDbAuthToken,
    changes: exported.changes,
    clientId: exported.importId,
    gatewayUrl: resolveActorSyncGatewayUrl(getBetterAuthBaseUrl()),
    importId: exported.importId,
    journalHead: exported.journalHead,
  });
  logger(import.meta.location).info("actor-db:guest-carry", {
    applied: sent.applied,
    rows: exported.changes.length,
    setAside: sent.setAside,
    source: exported.source,
  });
  return carried;
}
